import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { CheckboxGroup } from '~/eds/src/components/CheckboxGroup/CheckboxGroup';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { Option } from '~/eds/src/types/Option';
import { api } from '~/redux/api/createApi';
import { IntakeForm } from '~/redux/api/methods/ticket';
import { SupplierContact } from '~/redux/api/methods/wss/getSupplierCompany';
import { useAppSelector } from '~/rootState';

import { useTicketSupplierNegotiation } from './useTicketSupplierNegotiation';

const messages = defineMessages({
  select: {
    id: 'TicketDocumentActions.select',
    defaultMessage: 'Select recipients',
    description: 'used as a label for the recipients select',
  },
  selectContactsMessage: {
    id: 'TicketDocumentActions.selectContactsMessage',
    defaultMessage:
      'Select one or more contacts to receive the document on the supplier portal.',
    description:
      'Message explaining that users must select contacts for the supplier portal',
  },
  noSupplierContactsTitle: {
    id: 'TicketDocumentActions.noSupplierContactsTitle',
    defaultMessage: 'No contacts found for this Supplier',
    description: 'Title when supplier has no contacts',
  },
  noSupplierContactsMessage: {
    id: 'TicketDocumentActions.noSupplierContactsMessage',
    defaultMessage:
      'You must add at least one contact for this Supplier to send the document',
    description: 'Message when supplier has no contacts',
  },
  selectAllRecipients: {
    id: 'TicketDocumentActions.selectAllRecipients',
    defaultMessage: 'Select all',
    description: 'Label for select all recipients checkbox',
  },
  loadingContacts: {
    id: 'TicketDocumentActions.loadingContacts',
    defaultMessage: 'Loading contacts…',
    description: 'Loading message while fetching supplier contacts',
  },
  sendToSupplierPortalModalTitle: {
    id: 'TicketDocumentActions.sendToSupplierPortalModalTitle',
    defaultMessage: 'Send to Supplier Portal',
    description: 'Title for send to supplier portal modal',
  },
  sendButton: {
    id: 'TicketDocumentActions.sendButton',
    defaultMessage: 'Send',
    description: 'Send button text',
  },
  sendToSupplierPortalTooltip: {
    id: 'TicketDocumentActions.sendToSupplierPortalTooltip',
    defaultMessage: 'Select a contact to send',
    description: 'Tooltip when sending to supplier portal is disabled',
  },
  cancelButton: {
    id: 'TicketDocumentActions.cancelButton',
    defaultMessage: 'Cancel',
    description: 'Cancel button text',
  },
  sendToSupplierPortalWarning: {
    id: 'TicketDocumentActions.sendToSupplierPortalWarning',
    defaultMessage:
      'This document will be read-only when shared with the Supplier Portal. Editing, approvals, and external sharing are disabled until the supplier returns the document or it is retracted.',
    description:
      'Warning message about consequences of sending to supplier portal',
  },
  sendToSupplierPortalError: {
    id: 'TicketDocumentActions.sendToSupplierPortalError',
    defaultMessage:
      'Failed to send document to Supplier Portal. Please try again.',
    description: 'Error toast when sending to supplier portal fails',
  },
});

function getSupplierIdFromIntakeForm(
  intakeForm: IntakeForm | undefined,
): string | null {
  const supplierField = intakeForm?.wellKnownFields?.supplier;
  const innerValue = supplierField?.value as
    | { value?: { resource_id?: string } }
    | null
    | undefined;
  return innerValue?.value?.resource_id ?? null;
}

function contactToOption(contact: SupplierContact): Option<string> {
  const { name, email } = contact.attributes;
  const label = name ? `${name} (${email})` : email;
  return { value: contact.id, label };
}

interface SupplierRecipientsSelectProps {
  isLoading: boolean;
  contactOptions: Option<string>[];
  selectedContactIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

function SelectSupplierRecipients({
  isLoading,
  contactOptions,
  selectedContactIds,
  onSelectionChange,
}: SupplierRecipientsSelectProps) {
  const intl = useIntl();

  const computedState = (() => {
    if (isLoading) return 'loading';
    if (contactOptions.length === 0) return 'empty';
    return 'ready';
  })();

  const Content = {
    loading: () => (
      <Layout align="center" spacing={2}>
        <LoadingSpinner label={intl.formatMessage(messages.loadingContacts)} />
        <Text variant="body">
          {intl.formatMessage(messages.loadingContacts)}
        </Text>
      </Layout>
    ),
    empty: () => (
      <StatusMessage
        status="danger"
        title={intl.formatMessage(messages.noSupplierContactsTitle)}
        message={intl.formatMessage(messages.noSupplierContactsMessage)}
      />
    ),
    ready: () => (
      <CheckboxGroup
        enableCount
        label={intl.formatMessage(messages.selectAllRecipients)}
        name="supplier-contacts"
        options={contactOptions}
        value={selectedContactIds}
        onChange={(value) => onSelectionChange(value ?? [])}
      />
    ),
  }[computedState];

  return (
    <Layout direction="column" spacing={2}>
      <Content />
    </Layout>
  );
}

interface SendToSupplierPortalModalProps {
  onCancel: () => void;
  onSuccess: () => void;
}

interface TicketSlice {
  id: string;
  documentVersions: Array<{ id: string; versionNumber: number }>;
  document: { version: number };
  contractRecordUniqueId: string | null;
}

export const SendToSupplierPortalModal = ({
  onCancel,
  onSuccess,
}: SendToSupplierPortalModalProps) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  const ticket = useAppSelector(
    (state) => state.ticket,
  ) as unknown as TicketSlice;
  const ticketId = ticket.id;
  const currentVersion = ticket.documentVersions?.find(
    (v) => v.versionNumber === ticket.document?.version,
  );
  const versionId = currentVersion?.id ?? null;
  const contractId = ticket.contractRecordUniqueId ?? null;

  const { data: intakeForm } = api.endpoints.getTicketIntakeForm.useQuery(
    ticketId,
    { skip: !ticketId },
  );
  const supplierId = useMemo(
    () => getSupplierIdFromIntakeForm(intakeForm),
    [intakeForm],
  );
  const { data: supplierData, isLoading: isLoadingSupplier } =
    api.endpoints.getSupplierCompany.useQuery(
      { supplierId: supplierId!, includeContacts: true },
      { skip: !supplierId },
    );
  const { postShare, isLoading: isSending } = useTicketSupplierNegotiation();

  const contacts = useMemo(
    () => supplierData?.included ?? [],
    [supplierData?.included],
  );
  const contactOptions = useMemo(
    () => contacts.map(contactToOption),
    [contacts],
  );

  const canSend =
    supplierId &&
    contractId &&
    versionId &&
    selectedContactIds.length > 0 &&
    !isSending;

  const handleSend = async () => {
    if (!canSend || !supplierId || !versionId || !contractId) return;
    const selectedContacts = contacts.filter((c) =>
      selectedContactIds.includes(c.id),
    );
    try {
      await postShare({
        ticketId,
        versionId,
        contractId,
        supplierId,
        contacts: selectedContacts.map((c) => ({
          supplier_contact_wss_id: c.id,
          email: c.attributes.email,
        })),
      });
      setSelectedContactIds([]);
      onSuccess();
    } catch {
      toast({
        status: 'danger',
        message: intl.formatMessage(messages.sendToSupplierPortalError),
      });
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const isLoading = !intakeForm || (!!supplierId && isLoadingSupplier);

  return (
    <Modal
      data-testid="send-to-supplier-portal-modal"
      isVisible
      title={intl.formatMessage(messages.sendToSupplierPortalModalTitle)}
      width="m"
      onCancel={handleCancel}
      onHide={handleCancel}
      primaryAction={{
        text: intl.formatMessage(messages.sendButton),
        onClick: handleSend,
        disabled: !canSend,
        isLoading: isSending,
        tooltip:
          canSend || contactOptions.length === 0 || isSending
            ? undefined
            : intl.formatMessage(messages.sendToSupplierPortalTooltip),
      }}
      cancelText={intl.formatMessage(messages.cancelButton)}
      enableContentPadding
    >
      <Layout direction="column" spacing={4}>
        <Text variant="body">
          {intl.formatMessage(messages.sendToSupplierPortalWarning)}
        </Text>
        <Layout direction="column" spacing={2}>
          <Label>{intl.formatMessage(messages.select)}</Label>
          <SelectSupplierRecipients
            isLoading={isLoading}
            contactOptions={contactOptions}
            selectedContactIds={selectedContactIds}
            onSelectionChange={setSelectedContactIds}
          />
        </Layout>
      </Layout>
    </Modal>
  );
};
